import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, timeAgo } from "@/lib/format";
import { StarRating } from "@/components/StarRating";
import { ListingCard } from "@/components/ListingCard";
import { useFavorites } from "@/hooks/useFavorites";
import { PageLoader, EmptyState, ErrorState } from "@/components/common";
import { ShieldCheck, Package, Star } from "lucide-react";

export default function SellerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { favoriteIds, toggleFavorite } = useFavorites();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");

  const { data: seller, isLoading, isError, refetch } = useQuery({ queryKey: ["seller", id], queryFn: () => apiFetch(`/sellers/${id}`) });
  const { data: ratingsData } = useQuery({ queryKey: ["seller-ratings", id], queryFn: () => apiFetch(`/sellers/${id}/ratings`) });
  const { data: myRating } = useQuery({ queryKey: ["my-rating", id], queryFn: () => apiFetch(`/sellers/${id}/my-rating`), enabled: !!user && user?.id !== id });

  React.useEffect(() => { if (myRating?.stars) { setStars(myRating.stars); setComment(myRating.comment || ""); } }, [myRating]);

  const submitRating = async () => {
    try {
      await apiFetch(`/sellers/${id}/rate`, { method: "POST", body: JSON.stringify({ stars, comment: comment.trim() }) });
      toast.success("Rating submitted");
      qc.invalidateQueries({ queryKey: ["seller", id] });
      qc.invalidateQueries({ queryKey: ["seller-ratings", id] });
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;
  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center sm:flex-row sm:text-left">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-brand text-xl font-bold text-white">
          {seller.avatar_url ? <img src={resolveImage(seller.avatar_url)} alt="" className="h-full w-full object-cover" /> : initials(seller.name)}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-center gap-1.5 sm:justify-start">
            <h1 className="font-display text-2xl font-extrabold">{seller.name}</h1>
            {seller.verified && <ShieldCheck className="h-5 w-5 text-sky-600" />}
          </div>
          <div className="mt-1 flex items-center justify-center gap-2 text-sm text-muted-foreground sm:justify-start">
            <StarRating value={Math.round(seller.rating_avg || 0)} size={14} />
            {seller.rating_count ? `${seller.rating_avg} (${seller.rating_count} reviews)` : "No ratings yet"}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{seller.location} · {seller.sold_count || 0} sold</p>
        </div>
        {user?.is_admin && (
          <button onClick={() => navigate(`/admin?user=${id}`)} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary">View as admin</button>
        )}
      </div>

      {/* Rate */}
      {user && user.id !== id && !seller.banned && (
        <div className="mt-6 rounded-2xl border border-border p-4">
          <h2 className="flex items-center gap-1.5 font-semibold"><Star className="h-4 w-4 text-amber-400" />Rate this seller</h2>
          <div className="mt-2"><StarRating value={stars} interactive onChange={setStars} size={24} /></div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share your experience (optional)" className="mt-2 w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" data-testid="rating-comment" />
          <button onClick={submitRating} disabled={!stars} className="mt-2 rounded-xl bg-price px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="submit-rating">{myRating?.stars ? "Update rating" : "Submit rating"}</button>
        </div>
      )}

      {/* Listings */}
      <h2 className="mt-8 font-display text-lg font-bold">Listings</h2>
      <div className="mt-3">
        {seller.listings?.length === 0 ? (
          <EmptyState icon={Package} title="No active listings" />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {seller.listings.map((l, i) => (
              <ListingCard key={l.id} listing={{ ...l, seller }} index={i} favorited={favoriteIds.includes(l.id)} onToggleFavorite={user ? toggleFavorite : undefined} />
            ))}
          </div>
        )}
      </div>

      {/* Reviews */}
      {ratingsData?.ratings?.length > 0 && (
        <>
          <h2 className="mt-8 font-display text-lg font-bold">Reviews</h2>
          <div className="mt-3 space-y-3">
            {ratingsData.ratings.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{r.buyer_name}</span>
                  <span className="text-xs text-muted-foreground">{timeAgo(r.created_at)}</span>
                </div>
                <StarRating value={r.stars} size={14} />
                {r.comment && <p className="mt-1 text-sm text-foreground/80">{r.comment}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
